import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import './DirectFullscreenReel.css';

const DirectFullscreenReel = ({
  isOpen,
  onClose,
  feeds,
  renderFeedItem,
  initialIndex = 0,
  visibleCount,
  setVisibleCount
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);

  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const scrollTimeoutRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Auto-hide controls after 3s
  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    setShowControls(true);
    controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  // Scroll to a specific item
  const scrollToItem = useCallback((index) => {
    const el = itemRefs.current[index];
    if (!containerRef.current || !el) return;
    containerRef.current.scrollTo({
      top: el.offsetTop,
      behavior: 'smooth'
    });
    setCurrentIndex(index);
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  // Detect which item is in center
  const findCenterItem = useCallback(() => {
    if (!containerRef.current) return currentIndex;
    const scrollTop = containerRef.current.scrollTop;
    const containerHeight = containerRef.current.clientHeight;
    const centerY = scrollTop + containerHeight / 2;

    let closest = 0;
    let minDist = Infinity;
    itemRefs.current.forEach((el, i) => {
      if (!el) return;
      const elCenter = el.offsetTop + el.clientHeight / 2;
      const dist = Math.abs(centerY - elCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });
    return closest;
  }, [currentIndex]);

  // Scroll listener to update currentIndex
  const handleScroll = useCallback(() => {
    resetControlsTimeout();
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      const index = findCenterItem();
      if (index !== currentIndex) {
        setCurrentIndex(index);
      }
    }, 100);
  }, [findCenterItem, currentIndex, resetControlsTimeout]);

  // Touch swipe up/down to navigate
  const handleTouchStart = (e) => {
    containerRef.current.touchStartY = e.touches[0].clientY;
    resetControlsTimeout();
  };
  const handleTouchEnd = (e) => {
    const start = containerRef.current.touchStartY;
    if (start == null) return;
    const diff = start - e.changedTouches[0].clientY;
    const threshold = 50;
    if (Math.abs(diff) > threshold) {
      if (diff > 0 && currentIndex < feeds.length - 1) {
        scrollToItem(currentIndex + 1);
      } else if (diff < 0 && currentIndex > 0) {
        scrollToItem(currentIndex - 1);
      }
    }
    containerRef.current.touchStartY = null;
  };

  // Save current item for return-to-feed
  useEffect(() => {
    const item = feeds[currentIndex];
    if (item) {
      localStorage.setItem("lastViewedFeedId", item.id);
    }
  }, [currentIndex, feeds]);

  // Trigger load more if near end
  useEffect(() => {
    if (currentIndex >= feeds.length - 1) {
      setVisibleCount(prev => prev + 6);
    }
  }, [currentIndex, feeds.length, setVisibleCount]);

  // Scroll directly on first mount
  useLayoutEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      // Wait 1 frame for refs to populate
      requestAnimationFrame(() => {
        const el = itemRefs.current[initialIndex];
        if (el && containerRef.current) {
          containerRef.current.scrollTo({
            top: el.offsetTop,
            behavior: 'auto'
          });
          setCurrentIndex(initialIndex);
        }
      });

      resetControlsTimeout();
    }

    return () => {
      document.body.style.overflow = 'unset';
      clearTimeout(scrollTimeoutRef.current);
      clearTimeout(controlsTimeoutRef.current);
    };
  }, [isOpen, initialIndex, resetControlsTimeout]);

  // Scroll listener binding
  useEffect(() => {
    const container = containerRef.current;
    if (!isOpen || !container) return;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isOpen, handleScroll]);

  if (!isOpen) return null;

  return (
    <div className="direct-fullscreen-overlay">
      <div
        className="direct-fullscreen-container"
        ref={containerRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={resetControlsTimeout}
      >
        {feeds.map((item, index) => (
          <div
            key={item.id}
            ref={(el) => (itemRefs.current[index] = el)}
            className={`fullscreen-feed-item ${index === currentIndex ? 'active' : ''}`}
            data-index={index}
          >
            <div className="fullscreen-item-content">
              {renderFeedItem(item, index)}
            </div>
          </div>
        ))}
      </div>

      <div className={`direct-fullscreen-controls ${showControls ? 'visible' : ''}`}>
        <button className="control-btn close-btn" onClick={onClose} aria-label="Exit fullscreen">
          <i className="fi fi-rr-cross"></i>
        </button>

        <button
          className="control-btn sound-btn"
          onClick={() => {
            const newMute = !isMuted;
            setIsMuted(newMute);
            localStorage.setItem("isMuted", newMute);
          }}
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          <i className={`fi fi-rr-volume${localStorage.getItem("isMuted") === "true" ? "-mute" : ""}`}></i>
        </button>

        <div className="mode-indicator">
          <i className="fi fi-rr-play-alt"></i>
          <span>Party Mode</span>
        </div>
      </div>
    </div>
  );
};

export default DirectFullscreenReel;
